import React, { useState, useEffect } from 'react';
import {
  DndContext, closestCenter, MouseSensor, TouchSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// ── Design tokens ──────────────────────────────────────────────

const T = {
  bg:           'radial-gradient(140% 90% at 20% 0%, #FEFCF6 0%, #F5F2EA 55%, #EBE6D8 100%)',
  card:         '#FFFFFF',
  cardBorder:   'rgba(0,77,64,0.09)',
  cardShadow:   '0 2px 10px rgba(0,77,64,0.07), 0 1px 3px rgba(0,0,0,0.04)',
  cardShadowHov:'0 4px 18px rgba(0,77,64,0.11), 0 1px 4px rgba(0,0,0,0.06)',
  input:        '#FFFFFF',
  inputBorder:  'rgba(0,0,0,0.10)',
  inputFocus:   'rgba(255,188,0,0.55)',
  inputShadow:  'inset 0 1px 3px rgba(0,0,0,0.05)',
  inputFocusShadow: '0 0 0 3px rgba(255,188,0,0.18)',
  amber:        '#FFBC00',
  amberSoft:    'rgba(255,188,0,0.10)',
  amberDeep:    '#E8A800',
  teal:         '#004D40',
  tealSoft:     'rgba(0,77,64,0.07)',
  tealMid:      'rgba(0,77,64,0.14)',
  text:         '#1C1C1E',
  textMuted:    'rgba(28,28,30,0.52)',
  textDim:      'rgba(28,28,30,0.30)',
  border:       'rgba(0,0,0,0.07)',
  eyebrow:      'rgba(0,77,64,0.08)',
};

// ── CSS keyframes injector ─────────────────────────────────────

const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=DM+Serif+Display:ital@0;1&display=swap');

  .mse-card-enter { animation: mseCardIn 0.28s cubic-bezier(0.22,1,0.36,1) both; }
  @keyframes mseCardIn {
    from { opacity: 0; transform: translateY(8px); }
    to   { opacity: 1; transform: translateY(0); }
  }

  .mse-input:focus {
    border-color: rgba(255,188,0,0.55) !important;
    box-shadow: 0 0 0 3px rgba(255,188,0,0.18), inset 0 1px 3px rgba(0,0,0,0.04) !important;
    background: #FFFFFF !important;
  }

  .mse-entry-card {
    background: #FFFFFF;
    border: 1px solid rgba(0,77,64,0.09);
    border-radius: 14px;
    box-shadow: 0 2px 10px rgba(0,77,64,0.07), 0 1px 3px rgba(0,0,0,0.04);
    margin-bottom: 10px;
    overflow: hidden;
    position: relative;
    transition: box-shadow 0.18s ease, transform 0.18s ease;
  }
  .mse-entry-card::before {
    content: '';
    position: absolute;
    left: 0; top: 0; bottom: 0;
    width: 3px;
    background: #FFBC00;
    border-radius: 3px 0 0 3px;
    opacity: 0.6;
  }
  .mse-entry-card:active { transform: scale(0.99); }

  .mse-aufg-row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 9px 0;
    border-bottom: 1px solid rgba(0,0,0,0.06);
    transition: background 0.12s;
  }
  .mse-aufg-row:last-child { border-bottom: none; }

  .mse-add-btn {
    width: 100%;
    display: flex; align-items: center; justify-content: center; gap: 7px;
    padding: 12px;
    margin-top: 10px;
    border: 1.5px dashed rgba(0,77,64,0.18);
    border-radius: 12px;
    background: rgba(0,77,64,0.03);
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: rgba(0,77,64,0.52);
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s, color 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .mse-add-btn:active {
    background: rgba(255,188,0,0.08);
    border-color: rgba(255,188,0,0.4);
    color: #004D40;
  }

  .mse-save-btn {
    flex: 1.4;
    height: 50px;
    border-radius: 9999px;
    border: none;
    background: linear-gradient(135deg, #FFBC00 0%, #FFD040 100%);
    color: #004D40;
    font-family: inherit;
    font-size: 15px;
    font-weight: 700;
    cursor: pointer;
    box-shadow: 0 6px 20px rgba(255,188,0,0.38), 0 2px 6px rgba(255,188,0,0.20);
    transition: transform 0.15s, box-shadow 0.15s;
    -webkit-tap-highlight-color: transparent;
    letter-spacing: -0.1px;
  }
  .mse-save-btn:active {
    transform: scale(0.97);
    box-shadow: 0 3px 12px rgba(255,188,0,0.28);
  }

  .mse-cancel-btn {
    flex: 1;
    height: 50px;
    border-radius: 9999px;
    border: 1.5px solid rgba(0,77,64,0.18);
    background: transparent;
    color: rgba(0,77,64,0.7);
    font-family: inherit;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: background 0.15s, border-color 0.15s;
    -webkit-tap-highlight-color: transparent;
  }
  .mse-cancel-btn:active {
    background: rgba(0,77,64,0.05);
  }

  .mse-pager-btn {
    display: flex; align-items: center; gap: 5px;
    padding: 7px 12px; border-radius: 9999px;
    border: 1.5px solid rgba(0,77,64,0.12);
    background: transparent;
    font-family: inherit; font-size: 12px; font-weight: 600;
    cursor: pointer;
    transition: background 0.12s, border-color 0.12s;
    -webkit-tap-highlight-color: transparent;
  }
  .mse-pager-btn:not(:disabled):active {
    background: rgba(0,77,64,0.06);
  }
  .mse-pager-btn:disabled { opacity: 0.28; cursor: default; }

  .mse-back-btn {
    display: flex; align-items: center; gap: 5px;
    padding: 6px 0; border: none; background: none;
    font-family: inherit; font-size: 13px; font-weight: 600;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* ── Dragging: item styled directly (no DragOverlay portal) ── */
  .mse-entry-card.is-dragging {
    box-shadow:
      0 0 0 1.5px rgba(255,188,0,0.60),
      0 16px 48px rgba(255,188,0,0.22),
      0 10px 28px rgba(0,0,0,0.22) !important;
    border-color: rgba(255,188,0,0.45) !important;
    cursor: grabbing !important;
    z-index: 999;
  }

  .mse-aufg-row.is-dragging {
    background: rgba(255,188,0,0.07) !important;
    border-radius: 9px;
    outline: 1.5px solid rgba(255,188,0,0.45);
    cursor: grabbing !important;
    z-index: 999;
  }
`;

function StyleInjector() {
  useEffect(() => {
    const id = 'mse-styles';
    if (document.getElementById(id)) return;
    const el = document.createElement('style');
    el.id = id;
    el.textContent = STYLES;
    document.head.appendChild(el);
    return () => { /* keep styles for session */ };
  }, []);
  return null;
}

// ── Utility ────────────────────────────────────────────────────

function makeId() { return `id-${Math.random().toString(36).slice(2)}`; }
function withIds(arr) { return (arr || []).map(i => i._id ? i : { ...i, _id: makeId() }); }
function stripIds(arr) { return (arr || []).map(({ _id, ...r }) => r); }

// ── Icons ──────────────────────────────────────────────────────

const DragDotsIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 14 14" fill="none">
    <circle cx="5" cy="4"  r="1.2" fill="currentColor"/>
    <circle cx="9" cy="4"  r="1.2" fill="currentColor"/>
    <circle cx="5" cy="7"  r="1.2" fill="currentColor"/>
    <circle cx="9" cy="7"  r="1.2" fill="currentColor"/>
    <circle cx="5" cy="10" r="1.2" fill="currentColor"/>
    <circle cx="9" cy="10" r="1.2" fill="currentColor"/>
  </svg>
);

const ChevronR = ({ size = 7 }) => (
  <svg width={size} height={size * 12/7} viewBox="0 0 7 12" fill="none">
    <path d="M1 1l5 5-5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const ChevronL = ({ size = 7 }) => (
  <svg width={size} height={size * 12/7} viewBox="0 0 7 12" fill="none">
    <path d="M6 1L1 6l5 5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const TrashIcon = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none">
    <path d="M21 5.98C17.67 5.65 14.32 5.48 10.98 5.48c-1.98 0-3.96.1-5.94.3L3 5.98M8.5 4.97l.22-1.31C8.88 2.71 9 2 10.69 2h2.62C15 2 15.13 2.75 15.28 3.67l.22 1.3M18.85 9.14l-.65 10.07C18.09 20.78 18 22 15.21 22H8.79c-2.79 0-2.88-1.22-2.99-2.79L5.15 9.14"
      stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PlusIcon = () => (
  <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
    <path d="M6.5 1v11M1 6.5h11" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"/>
  </svg>
);

// ── Shared components ──────────────────────────────────────────

const inputStyle = {
  width: '100%',
  padding: '11px 14px',
  boxSizing: 'border-box',
  border: `1.5px solid ${T.inputBorder}`,
  borderRadius: 11,
  background: T.input,
  fontFamily: "'Geist', system-ui, -apple-system, sans-serif",
  fontSize: 14,
  color: T.text,
  outline: 'none',
  WebkitAppearance: 'none',
  boxShadow: T.inputShadow,
  transition: 'border-color 0.18s, box-shadow 0.18s, background 0.18s',
};

function FieldLabel({ children }) {
  return (
    <div style={{
      fontSize: 10,
      fontWeight: 700,
      letterSpacing: '0.11em',
      textTransform: 'uppercase',
      color: T.teal,
      opacity: 0.55,
      marginBottom: 7,
    }}>
      {children}
    </div>
  );
}

function FooterActions({ onCancel, onSave }) {
  return (
    <div style={{
      padding: '14px 16px',
      paddingBottom: 'calc(env(safe-area-inset-bottom, 0px) + 14px)',
      borderTop: `1px solid ${T.border}`,
      display: 'flex',
      gap: 10,
      flexShrink: 0,
      background: 'rgba(254,252,246,0.92)',
      backdropFilter: 'blur(8px)',
      WebkitBackdropFilter: 'blur(8px)',
    }}>
      <button onClick={onCancel} className="mse-cancel-btn">Abbrechen</button>
      <button onClick={onSave} className="mse-save-btn">Speichern</button>
    </div>
  );
}

// ── Aufgaben row (sortable) ────────────────────────────────────

function SortableAufgabeRow({ aufgabe, onChange, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: aufgabe._id });

  const dragTransform = isDragging
    ? `${CSS.Transform.toString(transform) || 'translate3d(0,0,0)'} scale(1.02) rotate(-0.4deg)`
    : CSS.Transform.toString(transform);

  return (
    <div
      ref={setNodeRef}
      className={`mse-aufg-row${isDragging ? ' is-dragging' : ''}`}
      style={{
        transform: dragTransform,
        transition: isDragging ? 'box-shadow 0.15s, outline 0.1s' : transition,
      }}
    >
      <span
        style={{
          color: isDragging ? T.amber : T.textDim,
          flexShrink: 0,
          touchAction: 'none',
          display: 'flex',
          padding: '2px 0',
          transition: 'color 0.15s',
        }}
        {...attributes}
        {...listeners}
        onClick={e => e.stopPropagation()}
      >
        <DragDotsIcon />
      </span>
      <input
        value={aufgabe.text}
        onChange={e => onChange(e.target.value)}
        placeholder="Aufgabe beschreiben…"
        className="mse-input"
        style={{
          ...inputStyle,
          flex: 1,
          padding: '9px 12px',
          fontSize: 13.5,
          background: isDragging ? 'transparent' : T.input,
        }}
      />
      <button
        onClick={e => { e.stopPropagation(); onDelete(); }}
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: T.textDim, padding: '4px 2px', flexShrink: 0, display: 'flex',
          transition: 'color 0.15s',
        }}
        onMouseEnter={e => e.currentTarget.style.color = '#E05252'}
        onMouseLeave={e => e.currentTarget.style.color = T.textDim}
      >
        <TrashIcon />
      </button>
    </div>
  );
}

// ── Aufgaben drag-and-drop list ────────────────────────────────

function AufgabenEditor({ bullets, onChange }) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const [localItems, setLocalItems] = React.useState(() =>
    (bullets || []).map((b, i) => ({
      _id: `aufg-${i}-${(typeof b === 'string' ? b : '').slice(0, 8)}`,
      text: typeof b === 'string' ? b : '',
    }))
  );
  React.useEffect(() => {
    setLocalItems((bullets || []).map((b, i) => ({
      _id: `aufg-${i}-${(typeof b === 'string' ? b : '').slice(0, 8)}`,
      text: typeof b === 'string' ? b : '',
    })));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(bullets)]);

  const push = (next) => {
    setLocalItems(next);
    onChange(next.map(a => a.text));
  };

  return (
    <div>
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={({ active, over }) => {
          if (!over || active.id === over.id) return;
          const oi = localItems.findIndex(a => a._id === active.id);
          const ni = localItems.findIndex(a => a._id === over.id);
          push(arrayMove(localItems, oi, ni));
        }}
      >
        <SortableContext items={localItems.map(a => a._id)} strategy={verticalListSortingStrategy}>
          {localItems.length === 0 && (
            <div style={{
              padding: '14px 0 8px',
              fontSize: 13,
              color: T.textDim,
              fontStyle: 'italic',
            }}>
              Noch keine Aufgaben — füge eine hinzu.
            </div>
          )}
          {localItems.map((a, i) => (
            <SortableAufgabeRow
              key={a._id}
              aufgabe={a}
              onChange={(val) => {
                const next = localItems.map((x, j) => j === i ? { ...x, text: val } : x);
                push(next);
              }}
              onDelete={() => push(localItems.filter((_, j) => j !== i))}
            />
          ))}
        </SortableContext>

      </DndContext>
      <button
        className="mse-add-btn"
        onClick={() => push([...localItems, { _id: `aufg-new-${Date.now()}`, text: '' }])}
      >
        <PlusIcon />
        Aufgabe hinzufügen
      </button>
    </div>
  );
}

// ── Sortable entry card (list view) ───────────────────────────

function SortableEntryRow({ entry, label, sublabel, idx, onTap, onDelete }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } =
    useSortable({ id: entry._id });

  const dragTransform = isDragging
    ? `${CSS.Transform.toString(transform) || 'translate3d(0,0,0)'} scale(1.04) rotate(-0.7deg)`
    : CSS.Transform.toString(transform);

  return (
    <div
      ref={setNodeRef}
      className={`mse-entry-card mse-card-enter${isDragging ? ' is-dragging' : ''}`}
      style={{
        transform: dragTransform,
        transition: isDragging ? 'box-shadow 0.15s' : transition,
        animationDelay: `${idx * 0.045}s`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 0 }}>
        {/* Drag handle */}
        <span
          style={{
            color: isDragging ? T.amber : T.textDim,
            flexShrink: 0,
            touchAction: 'none',
            display: 'flex',
            padding: '16px 10px 16px 14px',
            transition: 'color 0.15s',
          }}
          {...attributes}
          {...listeners}
          onClick={e => e.stopPropagation()}
        >
          <DragDotsIcon size={13} />
        </span>

        {/* Index badge */}
        <div style={{
          width: 22, height: 22, borderRadius: 999,
          background: T.tealSoft,
          border: `1px solid ${T.tealMid}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 10, fontWeight: 800, color: T.teal,
          flexShrink: 0, marginRight: 10,
        }}>
          {idx + 1}
        </div>

        {/* Text */}
        <button
          onClick={onTap}
          style={{
            flex: 1, background: 'none', border: 'none', padding: '14px 0',
            textAlign: 'left', cursor: 'pointer',
            fontFamily: "'Geist', system-ui, -apple-system, sans-serif",
            WebkitTapHighlightColor: 'transparent',
          }}
        >
          <div style={{
            fontSize: 14.5, fontWeight: 600, color: T.text,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            lineHeight: 1.3,
          }}>
            {label || '(Neuer Eintrag)'}
          </div>
          {sublabel && (
            <div style={{
              fontSize: 12, color: T.textMuted, marginTop: 2,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {sublabel}
            </div>
          )}
        </button>

        {/* Actions */}
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: T.textDim, padding: '14px 8px', flexShrink: 0, display: 'flex',
            transition: 'color 0.15s',
          }}
          onMouseEnter={e => e.currentTarget.style.color = '#E05252'}
          onMouseLeave={e => e.currentTarget.style.color = T.textDim}
        >
          <TrashIcon />
        </button>
        <span
          style={{ color: T.textDim, flexShrink: 0, display: 'flex', padding: '14px 14px 14px 4px', cursor: 'pointer' }}
          onClick={onTap}
        >
          <ChevronR size={6} />
        </span>
      </div>
    </div>
  );
}

// ── Entry detail view ──────────────────────────────────────────

function EntryView({ section, entryIdx, totalCount, onUpdate, onBack, sectionName, onPrev, onNext, onCancel, onSave }) {
  const getEntry = () => {
    if (section.typ === 'eintraege') return withIds(section.eintraege)[entryIdx];
    if (section.typ === 'kenntnisse') return withIds(section.kenntnisse)[entryIdx];
    return null;
  };

  const entry = getEntry();
  if (!entry) return null;

  const getTitle = () => {
    if (section.typ === 'eintraege') return entry.jobtitel || entry.rolle || entry.arbeitgeber || entry.firma || '';
    if (section.typ === 'kenntnisse') return entry.titel || '';
    return '';
  };

  const patchEntry = (patch) => {
    const updatedEntry = { ...entry, ...patch };
    if (section.typ === 'eintraege') {
      const arr = withIds(section.eintraege).map((e, i) => i === entryIdx ? updatedEntry : e);
      onUpdate({ ...section, eintraege: arr });
    } else if (section.typ === 'kenntnisse') {
      const arr = withIds(section.kenntnisse).map((e, i) => i === entryIdx ? updatedEntry : e);
      onUpdate({ ...section, kenntnisse: arr });
    }
  };

  const title = getTitle();

  return (
    <div style={{
      width: '100%', height: '100%',
      background: T.bg,
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Geist', system-ui, -apple-system, sans-serif",
      WebkitFontSmoothing: 'antialiased',
    }}>
      <StyleInjector />

      {/* Header */}
      <div style={{
        padding: '14px 16px 12px',
        borderBottom: `1px solid ${T.border}`,
        flexShrink: 0,
        background: 'rgba(254,252,246,0.6)',
        backdropFilter: 'blur(4px)',
      }}>
        {/* Back + breadcrumb */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 10 }}>
          <button className="mse-back-btn" onClick={onBack} style={{ color: T.teal }}>
            <ChevronL size={6} />
            <span>Zurück</span>
          </button>
          <span style={{ color: T.textDim, fontSize: 11 }}>·</span>
          <span style={{
            fontSize: 11, fontWeight: 700, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: T.teal, opacity: 0.6,
          }}>
            {sectionName}
          </span>
        </div>

        {/* Entry title */}
        <div style={{
          fontFamily: "'DM Serif Display', Georgia, serif",
          fontStyle: 'italic',
          fontSize: 22,
          fontWeight: 400,
          color: T.text,
          letterSpacing: -0.3,
          lineHeight: 1.2,
          marginBottom: 2,
        }}>
          {title || 'Neuer Eintrag'}
        </div>

        {/* Amber accent line */}
        <div style={{
          width: 32, height: 2, borderRadius: 2,
          background: `linear-gradient(90deg, ${T.amber}, transparent)`,
          marginTop: 6,
        }} />
      </div>

      {/* Pager */}
      <div style={{
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        padding: '9px 16px',
        borderBottom: `1px solid ${T.border}`,
        flexShrink: 0,
        background: T.tealSoft,
      }}>
        <button
          className="mse-pager-btn"
          onClick={onPrev}
          disabled={entryIdx === 0}
          style={{ color: entryIdx === 0 ? T.textDim : T.teal, borderColor: entryIdx === 0 ? T.border : T.tealMid }}
        >
          <ChevronL size={6} />
          <span>Vorheriger</span>
        </button>

        {/* Dot pager */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
          {Array.from({ length: Math.min(totalCount, 7) }, (_, i) => {
            const dotIdx = totalCount <= 7 ? i :
              entryIdx < 4 ? i :
              entryIdx > totalCount - 4 ? (totalCount - 7 + i) :
              (entryIdx - 3 + i);
            const isActive = dotIdx === entryIdx;
            return (
              <div key={i} style={{
                width: isActive ? 18 : 6,
                height: 6,
                borderRadius: 3,
                background: isActive ? T.amber : T.tealMid,
                transition: 'width 0.2s cubic-bezier(0.22,1,0.36,1), background 0.2s',
              }} />
            );
          })}
        </div>

        <button
          className="mse-pager-btn"
          onClick={onNext}
          disabled={entryIdx === totalCount - 1}
          style={{ color: entryIdx === totalCount - 1 ? T.textDim : T.teal, borderColor: entryIdx === totalCount - 1 ? T.border : T.tealMid }}
        >
          <span>Nächster</span>
          <ChevronR size={6} />
        </button>
      </div>

      {/* Fields */}
      <div style={{
        flex: 1, overflowY: 'auto',
        padding: '18px 16px',
        display: 'flex', flexDirection: 'column', gap: 20,
        scrollbarWidth: 'none',
      }}>
        {section.typ === 'eintraege' && (
          <>
            <div>
              <FieldLabel>Titel / Rolle</FieldLabel>
              <input
                value={entry.jobtitel || entry.rolle || ''}
                onChange={e => patchEntry({ jobtitel: e.target.value, rolle: undefined })}
                className="mse-input"
                style={inputStyle}
              />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <FieldLabel>Arbeitgeber</FieldLabel>
                <input
                  value={entry.arbeitgeber || entry.firma || ''}
                  onChange={e => patchEntry({ arbeitgeber: e.target.value, firma: undefined })}
                  className="mse-input"
                  style={inputStyle}
                />
              </div>
              <div>
                <FieldLabel>Datum</FieldLabel>
                <input
                  value={entry.datum || ''}
                  placeholder="06/2024 – Heute"
                  onChange={e => patchEntry({ datum: e.target.value })}
                  className="mse-input"
                  style={inputStyle}
                />
              </div>
            </div>
            <div>
              <FieldLabel>Aufgaben</FieldLabel>
              <AufgabenEditor
                bullets={entry.bullets || []}
                onChange={(neu) => patchEntry({ bullets: neu })}
              />
            </div>
          </>
        )}
        {section.typ === 'kenntnisse' && (
          <>
            <div>
              <FieldLabel>Kategorie</FieldLabel>
              <input
                value={entry.kategorie || ''}
                placeholder="z.B. Cloud & Infrastruktur"
                onChange={e => patchEntry({ kategorie: e.target.value })}
                className="mse-input"
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel>Titel</FieldLabel>
              <input
                value={entry.titel || ''}
                placeholder="z.B. Google Cloud Platform (GCP)"
                onChange={e => patchEntry({ titel: e.target.value })}
                className="mse-input"
                style={inputStyle}
              />
            </div>
            <div>
              <FieldLabel>Text</FieldLabel>
              <textarea
                value={entry.beschreibung || ''}
                onChange={e => patchEntry({ beschreibung: e.target.value })}
                className="mse-input"
                style={{ ...inputStyle, resize: 'none', minHeight: 110, lineHeight: 1.55 }}
                rows={4}
              />
            </div>
          </>
        )}
        <div style={{ height: 4 }} />
      </div>

      <FooterActions onCancel={onCancel} onSave={onSave} />
    </div>
  );
}

// ── Bullets inline editor ──────────────────────────────────────

function BulletsEditor({ section, onChange }) {
  const bullets = section.bullets || [];
  return (
    <div style={{
      background: T.tealSoft,
      border: `1px solid ${T.cardBorder}`,
      borderRadius: 12,
      padding: '2px 12px 6px',
    }}>
      {bullets.map((b, i) => (
        <div key={i} style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '9px 0',
          borderBottom: i < bullets.length - 1 ? `1px solid rgba(0,0,0,0.06)` : 'none',
        }}>
          <span style={{
            width: 6, height: 6, borderRadius: 99,
            background: T.amber, flexShrink: 0,
            boxShadow: '0 0 0 2px rgba(255,188,0,0.2)',
          }} />
          <input
            className="mse-input"
            style={{ ...inputStyle, flex: 1, padding: '9px 12px', fontSize: 13.5 }}
            value={b}
            onChange={e => {
              const next = [...bullets];
              next[i] = e.target.value;
              onChange({ ...section, bullets: next });
            }}
          />
          <button
            onClick={() => onChange({ ...section, bullets: bullets.filter((_, j) => j !== i) })}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              color: T.textDim, padding: '4px 2px', display: 'flex',
              transition: 'color 0.15s',
            }}
            onMouseEnter={e => e.currentTarget.style.color = '#E05252'}
            onMouseLeave={e => e.currentTarget.style.color = T.textDim}
          >
            <TrashIcon />
          </button>
        </div>
      ))}
      <button
        className="mse-add-btn"
        style={{ marginTop: 8, background: 'rgba(255,188,0,0.06)', borderColor: 'rgba(255,188,0,0.25)' }}
        onClick={() => onChange({ ...section, bullets: [...bullets, ''] })}
      >
        <PlusIcon />
        Punkt hinzufügen
      </button>
    </div>
  );
}

// ── List view ──────────────────────────────────────────────────

function ListView({ section, name, setName, onChange, onOpenEntry, onCancel, onSave }) {
  const sensors = useSensors(
    useSensor(MouseSensor, { activationConstraint: { distance: 5 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 5 } }),
  );

  const isListType = section.typ === 'eintraege' || section.typ === 'kenntnisse';

  const getEntries = () => {
    if (section.typ === 'eintraege') return withIds(section.eintraege || []);
    if (section.typ === 'kenntnisse') return withIds(section.kenntnisse || []);
    return [];
  };

  const getLabel = (e) => {
    if (section.typ === 'eintraege') return e.jobtitel || e.rolle || e.arbeitgeber || e.firma || '';
    if (section.typ === 'kenntnisse') return e.titel || '';
    return '';
  };

  const getSublabel = (e) => {
    if (section.typ === 'eintraege') {
      const parts = [e.arbeitgeber || e.firma, e.datum].filter(Boolean);
      return parts.join(' · ');
    }
    return '';
  };

  const updateEntries = (arr) => {
    if (section.typ === 'eintraege') onChange({ ...section, eintraege: arr });
    if (section.typ === 'kenntnisse') onChange({ ...section, kenntnisse: arr });
  };

  const handleDelete = (id) => {
    updateEntries(getEntries().filter(e => e._id !== id));
  };

  const handleAdd = () => {
    if (section.typ === 'eintraege') {
      onChange({ ...section, eintraege: [...(section.eintraege || []), { datum: '', jobtitel: '', arbeitgeber: '', bullets: [] }] });
    } else if (section.typ === 'kenntnisse') {
      onChange({ ...section, kenntnisse: [...(section.kenntnisse || []), { kategorie: '', titel: '', beschreibung: '' }] });
    } else if (section.typ === 'bullets') {
      onChange({ ...section, bullets: [...(section.bullets || []), ''] });
    }
  };

  const entries = getEntries();

  const typLabel = section.typ === 'eintraege' ? 'Einträge' :
                   section.typ === 'kenntnisse' ? 'Kenntnisse' : 'Punkte';

  return (
    <div style={{
      width: '100%', height: '100%',
      background: T.bg,
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Geist', system-ui, -apple-system, sans-serif",
      WebkitFontSmoothing: 'antialiased',
    }}>
      <StyleInjector />

      {/* Header */}
      <div style={{
        padding: '16px 16px 14px',
        borderBottom: `1px solid ${T.border}`,
        flexShrink: 0,
        background: 'rgba(254,252,246,0.65)',
        backdropFilter: 'blur(4px)',
      }}>
        {/* Eyebrow */}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 6,
          padding: '4px 10px', borderRadius: 999,
          background: T.tealSoft,
          border: `1px solid ${T.tealMid}`,
          marginBottom: 10,
        }}>
          <span style={{
            width: 5, height: 5, borderRadius: 99,
            background: T.amber, flexShrink: 0,
          }} />
          <span style={{
            fontSize: 10, fontWeight: 700, letterSpacing: '0.10em',
            textTransform: 'uppercase', color: T.teal, opacity: 0.75,
          }}>
            Lebenslauf · {typLabel}
          </span>
        </div>

        {/* Editable section name */}
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          style={{
            background: 'none', border: 'none', outline: 'none',
            fontFamily: "'DM Serif Display', Georgia, serif",
            fontStyle: 'italic',
            fontSize: 24, fontWeight: 400,
            color: T.text, letterSpacing: -0.4, width: '100%', padding: 0,
            lineHeight: 1.2,
          }}
        />

        {/* Amber underline */}
        <div style={{
          width: 28, height: 2.5, borderRadius: 2,
          background: `linear-gradient(90deg, ${T.amber}, transparent)`,
          marginTop: 8,
        }} />
      </div>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '14px 16px 0', scrollbarWidth: 'none' }}>
        {isListType ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={({ active, over }) => {
              if (!over || active.id === over.id) return;
              const oi = entries.findIndex(e => e._id === active.id);
              const ni = entries.findIndex(e => e._id === over.id);
              updateEntries(arrayMove(entries, oi, ni));
            }}
          >
            <SortableContext items={entries.map(e => e._id)} strategy={verticalListSortingStrategy}>
              {entries.length === 0 && (
                <div style={{
                  padding: '32px 0', textAlign: 'center',
                  fontSize: 13, color: T.textDim,
                  fontStyle: 'italic',
                }}>
                  Noch keine Einträge.
                </div>
              )}
              {entries.map((e, i) => (
                <SortableEntryRow
                  key={e._id}
                  entry={e}
                  label={getLabel(e)}
                  sublabel={getSublabel(e)}
                  idx={i}
                  onTap={() => onOpenEntry(i)}
                  onDelete={() => handleDelete(e._id)}
                />
              ))}
            </SortableContext>

          </DndContext>
        ) : (
          <BulletsEditor section={section} onChange={onChange} />
        )}

        {isListType && (
          <button className="mse-add-btn" onClick={handleAdd}>
            <PlusIcon />
            Eintrag hinzufügen
          </button>
        )}
        <div style={{ height: 18 }} />
      </div>

      <FooterActions onCancel={onCancel} onSave={onSave} />
    </div>
  );
}

// ── Main component ─────────────────────────────────────────────

export default function MobileSektionEditor({ sektion, onZurueck, onSave }) {
  const { sektion: sektionData } = sektion;

  const [section, setSection] = useState(() => {
    if (sektionData.typ === 'eintraege') return { ...sektionData, eintraege: withIds(sektionData.eintraege) };
    if (sektionData.typ === 'kenntnisse') return { ...sektionData, kenntnisse: withIds(sektionData.kenntnisse) };
    return { ...sektionData };
  });
  const [name, setName] = useState(sektionData.name || '');
  const [view, setView] = useState('list');
  const [activeIdx, setActiveIdx] = useState(0);

  const getCount = () => {
    if (section.typ === 'eintraege') return (section.eintraege || []).length;
    if (section.typ === 'kenntnisse') return (section.kenntnisse || []).length;
    return 0;
  };

  const handleSave = () => {
    let cleaned = { ...section, name };
    if (section.typ === 'eintraege') cleaned = { ...cleaned, eintraege: stripIds(section.eintraege) };
    if (section.typ === 'kenntnisse') cleaned = { ...cleaned, kenntnisse: stripIds(section.kenntnisse) };
    onSave(cleaned);
  };

  const openEntry = (idx) => {
    setActiveIdx(idx);
    setView('entry');
  };

  const totalCount = getCount();

  if (view === 'entry') {
    return (
      <EntryView
        section={section}
        entryIdx={activeIdx}
        totalCount={totalCount}
        onUpdate={setSection}
        onBack={() => setView('list')}
        sectionName={name}
        onPrev={() => setActiveIdx(i => Math.max(0, i - 1))}
        onNext={() => setActiveIdx(i => Math.min(totalCount - 1, i + 1))}
        onCancel={onZurueck}
        onSave={handleSave}
      />
    );
  }

  return (
    <ListView
      section={section}
      name={name}
      setName={setName}
      onChange={setSection}
      onOpenEntry={openEntry}
      onCancel={onZurueck}
      onSave={handleSave}
    />
  );
}
